import { useState } from "react";
import { MapPin, Phone, MessageCircle, Mail, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { BRAND, CONTACT, MAP_EMBED, whatsappUrl, contactEnquiryUrl } from "@/lib/site";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const contactTitle = t("contact_title");

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("err_name");
    if (!/^\d{10}$/.test(form.mobile.trim())) e.mobile = t("err_mobile");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    window.open(contactEnquiryUrl(form), "_blank");
    toast.success(t("toast_opening"), { description: t("toast_ready") });
    setForm({ name: "", mobile: "", email: "", message: "" });
  };

  const inputCls =
    "w-full rounded-lg border border-gold/20 bg-navy-900 px-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-gold transition-colors";

  return (
    <div className="pt-36 pb-24" data-testid="contact-page">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <p className="label-eyebrow">{t("contact_eyebrow")}</p>
        <h1 className="mt-4 font-serif font-light text-[clamp(2.6rem,7vw,6rem)] leading-[0.98] text-white">
          <MaskedLines lines={contactTitle.map((l, i) => (i === 1 ? <span key={i} className="text-gold-gradient italic">{l}</span> : l))} />
        </h1>
        <Reveal delay={0.4} className="mt-6 max-w-xl text-white/60 font-light text-lg">{t("contact_subtitle")}</Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Reveal className="grid sm:grid-cols-2 gap-4">
              <a href={whatsappUrl(CONTACT.primaryIntl, "Hello Mulywan!")} target="_blank" rel="noreferrer" data-testid="contact-primary-number" className="group rounded-2xl border border-gold/20 bg-navy-800 p-6 hover:border-gold/50 transition-colors">
                <Phone className="text-gold" size={22} />
                <p className="mt-4 text-xs uppercase tracking-[0.18em] text-white/45">{t("label_primary")}</p>
                <p className="mt-1 font-serif text-2xl text-white group-hover:text-gold transition-colors">+91 {CONTACT.primary}</p>
              </a>
              <a href={whatsappUrl(CONTACT.secondaryIntl, "Hello Mulywan!")} target="_blank" rel="noreferrer" data-testid="contact-secondary-number" className="group rounded-2xl border border-gold/20 bg-navy-800 p-6 hover:border-gold/50 transition-colors">
                <MessageCircle className="text-gold" size={22} />
                <p className="mt-4 text-xs uppercase tracking-[0.18em] text-white/45">{t("label_secondary")}</p>
                <p className="mt-1 font-serif text-2xl text-white group-hover:text-gold transition-colors">+91 {CONTACT.secondary}</p>
              </a>
            </Reveal>

            <Reveal delay={0.1} className="rounded-2xl border border-gold/20 bg-navy-800 p-6">
              <div className="flex gap-4">
                <MapPin className="text-gold shrink-0" size={22} />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-white/45">{t("reg_office")}</p>
                  <p className="mt-1.5 text-white/85 leading-relaxed">{BRAND.address}</p>
                </div>
              </div>
              <div className="mt-5 flex gap-4 border-t border-white/10 pt-5">
                <Clock className="text-gold shrink-0" size={22} />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-white/45">{t("working_hours_label")}</p>
                  <p className="mt-1.5 text-white/85">{t("working_hours_val")}</p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="overflow-hidden rounded-2xl border border-gold/20" data-testid="contact-map">
              <iframe
                title="Mulywan location"
                src={MAP_EMBED}
                width="100%"
                height="300"
                style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg) contrast(0.9)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="relative overflow-hidden rounded-3xl border border-gold/25 bg-navy-800 p-8 md:p-10">
              <div className="absolute -top-24 -right-24 h-56 w-56 radial-glow" />
              <div className="relative space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">{t("form_name")} *</label>
                  <input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("ph_name")} className={inputCls} />
                  {errors.name && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-name-error">{errors.name}</p>}
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">{t("form_mobile")} *</label>
                    <input data-testid="contact-mobile-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} inputMode="numeric" placeholder={t("ph_mobile")} className={inputCls} />
                    {errors.mobile && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-mobile-error">{errors.mobile}</p>}
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block flex items-center gap-1"><Mail size={12} /> {t("form_email")}</label>
                    <input data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("ph_email")} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">{t("form_message")}</label>
                  <textarea data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} placeholder={t("ph_message")} className={`${inputCls} resize-none`} />
                </div>
                <button type="submit" data-testid="contact-submit-btn" className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold py-4 text-sm uppercase tracking-[0.2em] font-semibold text-navy-900 hover:bg-gold-light transition-colors">
                  <Send size={16} /> {t("send_via_whatsapp")}
                </button>
                <p className="text-center text-[11px] text-white/35">{t("form_footnote_contact")}</p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
